import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpRight,
  ChevronRight,
  Cog,
  Database,
  FileCode2,
  GitBranch,
  LayoutPanelTop,
  Loader2,
  Target,
  Workflow,
  Zap,
} from 'lucide-react';

import { useSandboxChanges, useSandboxChangeRecords } from 'features/views/hooks/useOntologyViews';
import { reviewLabel, reviewValue } from '../utils/reviewLabels';
import {
  editorRouteForFamily,
  isConfigureOverlayFamily,
  isPreviewableFamily,
  branchOverlayDestination,
  orderedReviewViews,
  reviewViewPath,
} from '../utils/previewDestination';

/** Authored-artifact families → how they read to a user, plus an icon. */
const ARTIFACT_FAMILY_META = {
  view: { label: 'Apps', Icon: LayoutPanelTop },
  action: { label: 'Actions', Icon: Zap },
  agent: { label: 'Agents', Icon: Cog },
  automation: { label: 'Automations', Icon: Workflow },
  initiative_template: { label: 'Work templates', Icon: Target },
  ontology_schema_delta: { label: 'Ontology', Icon: GitBranch },
  operational_process_version: {
    label: 'Process maps',
    Icon: Workflow,
  },
  integration_definition: { label: 'Connections', Icon: Cog },
  data_retrieval_contract: { label: 'Data queries', Icon: Database },
  code_execution_program: { label: 'Programs', Icon: FileCode2 },
};

const familyMeta = (family) =>
  ARTIFACT_FAMILY_META[family] || {
    label: family ? family.replace(/_/g, ' ') : 'Other',
    Icon: GitBranch,
  };

const operationBadge = (artifact) => {
  if (artifact.operation === 'delete') {
    return {
      label: 'Removed',
      className: 'bg-rose-50 text-rose-700 ring-rose-200',
    };
  }
  if (artifact.is_new) {
    return {
      label: 'New',
      className: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    };
  }
  return {
    label: 'Updated',
    className: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  };
};

const formatCount = (value) => Number(value || 0).toLocaleString();

/** Open the Configure tab that owns this family, preserving the active Sandbox. */
const artifactEditorPath = (artifact, sandbox, context, definitions, canOpenEditors, previews) => {
  const route = editorRouteForFamily(artifact?.family);
  if (!route || artifact.operation === 'delete' || (!canOpenEditors && artifact.family !== 'view')) return '';
  return branchOverlayDestination({
    sandboxId: sandbox?.sandbox_id || sandbox?.id || '',
    revisionId: context?.revisionId,
    approvalId: context?.approvalId,
    artifacts: [artifact],
    definitions,
    canOpenEditors,
    previews,
  });
};

const NOT_PREVIEWABLE_NOTE = 'Expand this item to inspect its saved definition.';

const RECORD_SAMPLE_LIMIT = 5;
const RECORD_PAGE_STEP = 25;
const RECORD_PAGE_MAX = 100; // The changes/records API caps one page at 100.

const recordHeadline = (record) => record.display_name || record.primary_key_value || record.id || 'Record';

const FieldDiff = ({ field }) => {
  const hasBefore = field.before !== null && field.before !== undefined && field.before !== '';
  return (
    <div className="flex min-w-0 items-baseline gap-1.5 py-0.5">
      <span className="shrink-0 truncate text-[11px] text-gray-400">{reviewLabel(field.label || field.name)}</span>
      <span className="flex min-w-0 items-baseline gap-1 text-[11px]">
        {hasBefore ? (
          <>
            <span title={String(field.before)} className="truncate text-gray-400 line-through">{reviewValue(field.before)}</span>
            <span className="text-gray-300">→</span>
          </>
        ) : null}
        <span title={field.after == null ? undefined : String(field.after)} className="truncate font-medium text-gray-800">{reviewValue(field.after)}</span>
      </span>
    </div>
  );
};

const statusBadge = (status) =>
  status === 'new'
    ? {
        label: 'New',
        className: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
      }
    : {
        label: 'Updated',
        className: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
      };

/** Lazily-loaded sample of changed records for one type, with before → after. */
const ChangedRecordsSample = ({ sandboxId, typeName, context }) => {
  // A reviewer checking one corrected record must be able to reach it.
  const [limit, setLimit] = useState(RECORD_SAMPLE_LIMIT);
  const { data, isLoading, isError, error } = useSandboxChangeRecords({
    sandboxId,
    overlayId: context?.revisionId,
    approvalId: context?.approvalId,
    typeName,
    kind: 'object',
    limit,
    enabled: true,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-3 text-xs text-gray-500">
        <Loader2 size={13} className="animate-spin" />
        Loading records…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-3 py-3 text-xs text-rose-700">{error?.message || 'Could not load records.'}</div>
    );
  }

  const records = Array.isArray(data?.records) ? data.records : [];
  const total = Number(data?.total || records.length);

  if (records.length === 0) {
    return <div className="px-3 py-3 text-xs text-gray-500">No record details available.</div>;
  }

  return (
    <div className="space-y-2 px-3 py-3">
      <div className="text-[11px] text-gray-400">
        Showing {records.length} of {formatCount(total)}
      </div>
      <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
        {records.map((record, index) => {
          const fields = Array.isArray(record.fields) ? record.fields : [];
          const badge = statusBadge(record.status);
          return (
            <li
              key={record.id || index}
              className="rounded-lg border border-gray-100 bg-gray-50/60 px-2.5 py-2"
            >
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">
                  {recordHeadline(record)}
                </span>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${badge.className}`}
                >
                  {badge.label}
                </span>
              </div>
              {fields.length > 0 ? (
                <div className="mt-1">
                  {fields.map((field) => (
                    <FieldDiff key={field.name} field={field} />
                  ))}
                </div>
              ) : (
                <div className="mt-1 text-[11px] text-gray-400">No field-level changes.</div>
              )}
            </li>
          );
        })}
      </ul>
      {records.length < total && limit < RECORD_PAGE_MAX ? (
        <button
          type="button"
          onClick={() => setLimit((current) => Math.min(current + RECORD_PAGE_STEP, RECORD_PAGE_MAX))}
          className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800"
        >
          Show more
        </button>
      ) : null}
    </div>
  );
};

/** A single affected object type that expands to a lazily-loaded record sample. */
const ExpandableTypeRow = ({ sandboxId, typeName, context }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-2 px-2.5 py-2 text-left transition hover:bg-gray-50"
      >
        <ChevronRight
          className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
          aria-hidden
        />
        <span className="min-w-0 truncate text-sm font-medium text-gray-700">{reviewLabel(typeName)}</span>
        <span className="ml-auto text-[11px] text-gray-400">{isOpen ? 'Hide' : 'View records'}</span>
      </button>
      {isOpen ? (
        <div className="border-t border-gray-100">
          <ChangedRecordsSample sandboxId={sandboxId} typeName={typeName} context={context} />
        </div>
      ) : null}
    </div>
  );
};

const DataCountRow = ({ label, value, tone = 'gray' }) => {
  if (!value) return null;
  const toneClass = tone === 'rose' ? 'text-rose-700' : 'text-gray-900';
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${toneClass}`}>{formatCount(value)}</span>
    </div>
  );
};

/**
 * Draft changes panel. Authored artifacts (views, actions, automations, …) are listed by
 * name from the sandbox change ledger — cheap and bounded. Instance data is shown as
 * aggregate counts only, never materialized, so mass edits stay light to display.
 */
const SandboxChangesPanel = ({
  sandbox,
  changesData = null,
  readOnly = false,
  context,
  definitions = [],
  canOpenEditors = true,
}) => {
  const sandboxId = sandbox?.sandbox_id || null;
  const query = useSandboxChanges({
    sandboxId,
    overlayId: context?.revisionId,
    approvalId: context?.approvalId,
    enabled: Boolean(sandboxId && !changesData),
  });
  const data = changesData || query.data;
  const isLoading = changesData ? false : query.isLoading;
  const isError = changesData ? false : query.isError;
  const error = changesData ? null : query.error;

  const artifacts = useMemo(() => (Array.isArray(data?.artifacts) ? data.artifacts : []), [data]);
  const reviewViews = useMemo(() => orderedReviewViews(data?.review?.review_views), [data]);
  const reviewContext = {
    sandboxId: sandboxId || '',
    revisionId: context?.revisionId,
    approvalId: context?.approvalId,
  };
  const dataChanges = data?.data && typeof data.data === 'object' ? data.data : {};

  const artifactsByFamily = useMemo(() => {
    const groups = new Map();
    artifacts.forEach((artifact) => {
      const family = artifact.family || 'other';
      if (!groups.has(family)) groups.set(family, []);
      groups.get(family).push(artifact);
    });
    return Array.from(groups.entries());
  }, [artifacts]);

  const objectTypes = Array.isArray(dataChanges.affected_object_types)
    ? dataChanges.affected_object_types
    : [];

  const totalDataChanges =
    Number(dataChanges.object_upsert_count || 0) +
    Number(dataChanges.object_delete_count || 0) +
    Number(dataChanges.relationship_upsert_count || 0) +
    Number(dataChanges.relationship_delete_count || 0);

  const hasArtifacts = artifacts.length > 0;
  const hasData = totalDataChanges > 0;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
        <Loader2 size={15} className="animate-spin" />
        Loading changes…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {error?.message || 'Could not load draft changes.'}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full space-y-6 overflow-y-auto p-6">
      {sandbox?.description ? (
        <p className="max-w-2xl text-sm leading-relaxed text-gray-600">{sandbox.description}</p>
      ) : null}

      {reviewViews.length > 0 ? (
        <section aria-labelledby="branch-review-views">
          <h3 id="branch-review-views" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Review in
          </h3>
          <ul className="mt-2 divide-y divide-gray-50 rounded-xl border border-gray-200 bg-white">
            {reviewViews.map((row) => {
              const path = reviewViewPath(row, reviewContext);
              const label = (
                <span className="flex min-w-0 items-center gap-1.5">
                  <LayoutPanelTop className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                  <span className="min-w-0 truncate text-sm font-medium text-gray-800">{row.name || row.view_id}</span>
                  {path ? <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden /> : null}
                </span>
              );
              const tag = row.primary ? (
                <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
                  Primary
                </span>
              ) : null;
              return (
                <li key={row.view_id || row.name}>
                  {path ? (
                    <Link to={path} className="flex items-center justify-between gap-3 px-3 py-2 transition hover:bg-gray-50">
                      {label}
                      {tag}
                    </Link>
                  ) : (
                    <div className="flex items-center justify-between gap-3 px-3 py-2">
                      {label}
                      {tag}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {!hasArtifacts && !hasData ? (
        <p className="text-sm text-gray-500">No changes recorded in this draft yet.</p>
      ) : null}

      {hasArtifacts ? (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Apps &amp; logic</h3>
          <div className="mt-2 space-y-3">
            {artifactsByFamily.map(([family, items]) => {
              const meta = familyMeta(family);
              const { Icon } = meta;
              return (
                <div key={family} className="rounded-xl border border-gray-200 bg-white">
                  <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
                    <Icon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                    <span className="text-sm font-medium text-gray-800">{meta.label}</span>
                    {readOnly || isPreviewableFamily(family) || isConfigureOverlayFamily(family) ? null : (
                      <span className="text-[11px] text-gray-400" title={NOT_PREVIEWABLE_NOTE}>
                        Saved definition
                      </span>
                    )}
                    <span className="ml-auto text-xs text-gray-400">{items.length}</span>
                  </div>
                  <ul className="divide-y divide-gray-50">
                    {items.map((artifact, index) => {
                      const badge = operationBadge(artifact);
                      const editorPath = readOnly
                        ? ''
                        : artifactEditorPath(
                            artifact,
                            sandbox,
                            context,
                            definitions,
                            canOpenEditors,
                            data?.previews
                          );
                      const definition = definitions.find(
                        (row) =>
                          row.family === family &&
                          ([row.id, row.sandbox_artifact_id].includes(artifact.artifact_ref) ||
                            row.name === artifact.name)
                      );
                      const label = artifact.name || <span className="text-gray-400">Unnamed</span>;
                      const operationTag = (
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      );
                      return (
                        <li key={`${family}-${artifact.name || index}`}>
                          {editorPath ? (
                            <Link
                              to={editorPath}
                              className="flex items-center justify-between gap-3 px-3 py-2 transition hover:bg-gray-50"
                            >
                              <span className="flex min-w-0 items-center gap-1.5">
                                <span className="min-w-0 truncate text-sm font-medium text-gray-800">
                                  {label}
                                </span>
                                <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
                              </span>
                              {operationTag}
                            </Link>
                          ) : definition ? (
                            <details className="px-3 py-2 text-sm text-gray-700">
                              <summary className="cursor-pointer">
                                {label} <span className="ml-2">{operationTag}</span>
                              </summary>
                              <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-gray-50 p-3 text-xs">
                                {JSON.stringify(definition.payload || definition.review_diff || {}, null, 2)}
                              </pre>
                            </details>
                          ) : (
                            <div className="flex items-center justify-between gap-3 px-3 py-2">
                              <span className="min-w-0 truncate text-sm text-gray-700">{label}</span>
                              {operationTag}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {hasData ? (
        <section>
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Data</h3>
          </div>
          <div className="mt-2 rounded-xl border border-gray-200 bg-white px-4 py-3">
            <DataCountRow label="Records added or updated" value={dataChanges.object_upsert_count} />
            <DataCountRow label="Records deleted" value={dataChanges.object_delete_count} tone="rose" />
            <DataCountRow label="Links added or updated" value={dataChanges.relationship_upsert_count} />
            <DataCountRow label="Links deleted" value={dataChanges.relationship_delete_count} tone="rose" />
          </div>

          {objectTypes.length > 0 ? (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Record types
              </div>
              <div className="mt-1.5 space-y-1.5">
                {objectTypes.map((typeName) => (
                  <ExpandableTypeRow
                    key={`obj-${typeName}`}
                    sandboxId={sandboxId}
                    typeName={typeName}
                    context={context}
                  />
                ))}
              </div>
            </div>
          ) : null}
          <p className="mt-2 text-[11px] leading-relaxed text-gray-400">
            {objectTypes.length > 0
              ? 'Counts are exact. Expand a record type to review the changed records. Select Show more to see additional records.'
              : 'Counts are exact.'}
          </p>
        </section>
      ) : null}
    </div>
  );
};

export default SandboxChangesPanel;
